import { useTranslation } from 'react-i18next';
import { useProjects, useProjectMembers } from '@/features/projects/public';
import { useBoards, useBoardStatuses, useBoardGroups } from '@/features/boards/public';
import type { WidgetDef, WidgetScope } from '../types';

/**
 * Resolves the raw IDs the backend returns as `labels[]` (assignee/status/
 * board/group/project — see WidgetDataService, which never enriches with
 * names) into the display names the modules that own each dimension already
 * expose. Falls back to the raw ID when the owning project/board isn't
 * resolvable (e.g. a tenant-wide widget with no project pinned) or the id
 * isn't found in the loaded list.
 */
export function useWidgetLabelResolver(
  widget: WidgetDef,
  scope: WidgetScope,
): (rawLabel: string) => string {
  const { t } = useTranslation('dashboard');
  const projectId =
    widget.config.scope?.projectId ?? (scope.type !== 'tenant' ? scope.projectId : undefined);
  const boardId =
    widget.config.scope?.boardId ?? (scope.type === 'board' ? scope.boardId : undefined);

  const { data: projects } = useProjects();
  const { data: members } = useProjectMembers(projectId ?? '');
  const { boards } = useBoards(projectId ?? '');
  const { statuses } = useBoardStatuses(boardId ?? '');
  const { groups } = useBoardGroups(boardId ?? '');

  return (rawLabel: string): string => {
    switch (widget.config.groupBy) {
      case 'Project':
        return (projects ?? []).find((p) => p.id === rawLabel)?.name ?? rawLabel;
      case 'Assignee': {
        const member = (members ?? []).find((m) => m.userId === rawLabel);
        return member?.displayName || member?.email || rawLabel;
      }
      case 'Board':
        return boards.find((b) => b.id === rawLabel)?.name ?? rawLabel;
      case 'Status':
        return statuses.find((s) => s.id === rawLabel)?.name ?? rawLabel;
      case 'Group':
        return groups.find((g) => g.id === rawLabel)?.name ?? rawLabel;
      case 'Priority':
        return t(`filters.priorities.${rawLabel}`, { defaultValue: rawLabel });
      default:
        return rawLabel;
    }
  };
}
